/** "Secondary connection" section for the MeterDetail panel (Phase 3 step 17).
 *  Shows the meter's derived transformer (via the secondary graph), its
 *  confidence/tier, and whether it agrees with the imported XFMR_ID. Degrades to
 *  "not computed" when no published run covers the meter's feeder. */

import type { ConnectivityTier, SecondaryTracePath } from '../../types/secondary.ts'
import { TIER_COLORS } from '../../types/secondary.ts'

interface SecondaryConnectionSectionProps {
  trace: SecondaryTracePath | undefined
  onNavigate: (type: string, id: number) => void
}

export function SecondaryConnectionSection({ trace, onNavigate }: SecondaryConnectionSectionProps) {
  return (
    <div className="border-t border-gray-200 pt-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
        Secondary Connection
      </p>
      {!trace || !trace.available ? (
        <div className="text-sm text-gray-400">Not computed</div>
      ) : (
        <div className="space-y-2">
          {trace.transformer_id != null ? (
            <button
              onClick={() => onNavigate('Transformer', trace.transformer_id!)}
              className="w-full text-left p-2 rounded bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-violet-400" />
                <span className="text-sm font-medium text-gray-700">
                  Transformer #{trace.transformer_id}
                </span>
                <TierBadge tier={trace.tier} />
              </div>
              <div className="text-xs text-gray-400 ml-4">via secondary graph</div>
            </button>
          ) : (
            <div className="p-2 rounded bg-gray-50 text-sm text-gray-500">
              No derived transformer (unrooted)
            </div>
          )}

          <div className="space-y-1 text-sm">
            <DetailRow label="Confidence" value={formatConfidence(trace.confidence)} />
            <DetailRow label="Path Method" value={trace.path_method} />
            <DetailRow
              label="Attach Distance"
              value={trace.attach_distance_ft != null ? `${trace.attach_distance_ft.toFixed(1)} ft` : null}
            />
            <AgreesRow agrees={trace.agrees_with_xfmr_id} />
          </div>
        </div>
      )}
    </div>
  )
}

/** Small chip for the derived rooting tier (auto/flagged/review). */
export function TierBadge({ tier }: { tier: ConnectivityTier | null | undefined }) {
  if (!tier) return null
  const label: Record<ConnectivityTier, string> = {
    auto: 'Auto',
    flagged: 'Flagged',
    review: 'Review',
  }
  return (
    <span
      className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium text-white"
      style={{ backgroundColor: TIER_COLORS[tier] }}
    >
      {label[tier]}
    </span>
  )
}

function AgreesRow({ agrees }: { agrees: boolean | null | undefined }) {
  if (agrees === null || agrees === undefined) return null
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">Agrees with XFMR_ID</span>
      <span className={`font-medium ${agrees ? 'text-green-600' : 'text-amber-600'}`}>
        {agrees ? 'Yes' : 'No'}
      </span>
    </div>
  )
}

function formatConfidence(confidence: number | null | undefined): string | null {
  if (confidence === null || confidence === undefined) return null
  return `${(confidence * 100).toFixed(0)}%`
}

function DetailRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value === null || value === undefined) return null
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-800 font-medium">{String(value)}</span>
    </div>
  )
}
